/**
 * Маркетинговые пиксели — FB и LinkedIn, по доке UA-команды («Sales Воронка Димы: аналитика»).
 * GA из той же доки живёт отдельным файлом (ga.ts) со своим выключателем — сюда её не добавлять.
 *
 * Это НЕ продуктовая аналитика: analytics.ts отвечает на «что происходит в продукте», этот файл —
 * рекламным площадкам на «кого дообучать». Разные потребители, разные события, разная приватность —
 * поэтому два файла, а не один трекер с двумя выходами.
 *
 * Включение — только явным VITE_MARKETING_PIXELS (1/true/yes; 0/false/no/off и пусто — выключено, см.
 * utils/marketingConfig.ts, flagOn). Не import.meta.env.PROD: `npm run build` гоняют и локально, и на
 * стейдже, а один залётный PageView с машины разработчика — это чужие рекламные данные, испорченные
 * молча. Выключено = полный no-op, скрипты площадок даже не загружаются. И даже включённые, пиксели
 * говорят только с прод-хоста — режим force аналитики их не открывает (analyticsGate.marketingAllowed).
 *
 * События — СТАНДАРТНЫЕ мета-события (Lead / InitiateCheckout / Purchase), не кастомные: на этом
 * настаивает дока, потому что оптимизация кампаний Меты работает только на стандартных.
 */

import { marketingAllowed } from '@/utils/analyticsGate'
import { deviceId } from '@/utils/deviceId'
import { FUNNEL_ASSIGNMENT, FUNNEL_PARAMS, FUNNEL_TYPE } from '@/utils/funnel'
import { StorageKeys, storageService } from '@/utils/localStorageService'
import {
  flagOn,
  leadAlreadySent,
  leadEventId,
  leadParams,
  leadSlot,
  parseLeadValues,
  withLeadSent,
} from '@/utils/marketingConfig'

const ENABLED = flagOn(import.meta.env.VITE_MARKETING_PIXELS)

/** Ценность Lead по воронке (utils/marketingConfig.ts, parseLeadValues). Пусто — Lead без value. */
const LEAD_VALUES = parseLeadValues(import.meta.env.VITE_FB_LEAD_VALUES)

/**
 * Два пикселя ПАРАЛЛЕЛЬНО, из доки дословно: 410685974639473 — «Web Onboarding - Founders and
 * Clevels», на нём оптимизироваться; 905817854468384 — «Web Onboarding - Sales», данных там сильно
 * меньше, и он обучается за счёт первого. fbq('init') обоих означает, что каждый track уходит в оба.
 * Env-переопределение существует ради проверки проводки фейковым id, не ради окружений.
 */
const FB_PIXEL_IDS = ((import.meta.env.VITE_FB_PIXEL_IDS as string | undefined) || '410685974639473,905817854468384')
  .split(',')
  .map((id) => id.trim())
  .filter(Boolean)

const LI_PARTNER_ID = (import.meta.env.VITE_LI_PARTNER_ID as string | undefined) || '5651330'
const LI_CONVERSION = { lead: 27384026, purchase: 27384034 } as const

/**
 * Доля суммы, которую видит Мета как value покупки. Была 92% (правило исходной доки), 26.08.2026
 * UA-команда попросила 70% — для FB и LinkedIn. У LinkedIn самого value из тега не шлётся вовсе:
 * их снипет несёт только conversion_id, сумма конверсии настраивается в Campaign Manager — так что
 * кодом правка касается одной Меты. GA осталась на 92% (см. ga.ts) — там просьба не звучала.
 */
const REPORTED_SHARE = 0.7

declare global {
  interface Window {
    fbq?: ((...args: unknown[]) => void) & { queue?: unknown[]; q?: unknown[] }
    _fbq?: unknown
    lintrk?: ((...args: unknown[]) => void) & { q?: unknown[] }
    _linkedin_partner_id?: string
    _linkedin_data_partner_ids?: string[]
  }
}

const loadScript = (src: string) => {
  const tag = document.createElement('script')
  tag.async = true
  tag.src = src
  document.head.appendChild(tag)
}

let started = false

/**
 * Ленивая загрузка обоих снипетов при первом же вызове. Оба скрипта до загрузки подменяются очередью
 * (стандартный приём их же снипетов), так что событие, случившееся раньше сети, не теряется.
 */
const start = () => {
  if (started) return
  started = true

  /* Мета: стаб обязан быть ИХ каноническим, а не похожим. Их fbevents.js вычитывает очередь один
     раз при загрузке и дальше ждёт, что стаб сам проверит callMethod на каждом вызове. Наш первый
     стаб пушил в очередь всегда — и всё, что стреляло после загрузки скрипта (Lead, InitiateCheckout,
     Purchase), молча умирало в ней. Замерено UA-командой на проде: LinkedIn регу затрекал (их скрипт
     подменяет window.lintrk собой), Мета — нет. PageView маскировал баг, успевая в очередь до вычитки. */
  const fbq: NonNullable<Window['fbq']> = function (this: unknown, ...args: unknown[]) {
    const self = fbq as unknown as { callMethod?: (...a: unknown[]) => void; queue: unknown[] }
    if (self.callMethod) self.callMethod.apply(self, args)
    else self.queue.push(args)
  }
  fbq.queue = []
  ;(fbq as unknown as Record<string, unknown>).push = fbq
  ;(fbq as unknown as Record<string, unknown>).loaded = true
  ;(fbq as unknown as Record<string, unknown>).version = '2.0'
  window.fbq = window.fbq ?? fbq
  window._fbq = window._fbq ?? window.fbq
  for (const id of FB_PIXEL_IDS) window.fbq('init', id)
  loadScript('https://connect.facebook.net/en_US/fbevents.js')

  // LinkedIn: их снипет из доки, переписанный без getElementsByTagName-вставки.
  window._linkedin_partner_id = LI_PARTNER_ID
  window._linkedin_data_partner_ids = window._linkedin_data_partner_ids ?? []
  window._linkedin_data_partner_ids.push(LI_PARTNER_ID)
  if (!window.lintrk) {
    const lintrk: NonNullable<Window['lintrk']> = (...args: unknown[]) => {
      ;(lintrk.q as unknown[]).push(args)
    }
    lintrk.q = []
    window.lintrk = lintrk
  }
  loadScript('https://snap.licdn.com/li.lms-analytics/insight.min.js')
}

/** Одна страница SPA-навигации. Дока просит пиксель «на всех страницах флоу» — для SPA это route change. */
/*
 * ТИП ВОРОНКИ НА КАЖДОМ СОБЫТИИ FB (2026-09-21, utils/funnel.ts). Цель экспериментов с воронками —
 * CAC, а CAC считается на стороне рекламной площадки: кастомная конверсия «Purchase, где
 * funnel_type = N» и есть путь к CAC по воронкам. Имена событий НЕ меняются — на них уже
 * оптимизируются кампании, и переименование обнулило бы их обучение. LinkedIn параметров не
 * принимает: его конверсии — фиксированные id, разбить по воронкам можно только отдельными id в их
 * кабинете, поэтому lintrk ниже метку не несёт.
 */
/* С 2026-09-28 в FUNNEL_PARAMS ещё и сплит-тест: `funnel_test` и `split_pool` у назначенных пулом,
   `split_pool: 'override'` у QA (utils/funnel.ts, funnelTags) — кастомная конверсия по паре «тест ×
   пул» отделяет кампанию A от кампании B и обе — от проверок. */
export const pixelsPageView = () => {
  if (!ENABLED || !marketingAllowed()) return
  start()
  window.fbq?.('track', 'PageView', FUNNEL_PARAMS)
}

/**
 * Рега — контакт онбординга отправлен. У Меты это стандартный Lead, у LinkedIn — конверсия из доки.
 *
 * ОДИН РАЗ на браузер и пару «воронка × тест» (utils/marketingConfig.ts, leadSlot) — и для Меты, и для
 * LinkedIn: повторная отправка формы не новый лид. eventID стабильный для пары, чтобы серверный дубль
 * через Conversions API, когда он появится, склеился с этим. value и currency — только если для
 * воронки задана цена в VITE_FB_LEAD_VALUES; без переменной параметры те же, что до сплит-теста.
 */
export const pixelsLead = () => {
  if (!ENABLED || !marketingAllowed()) return
  const slot = leadSlot(FUNNEL_ASSIGNMENT)
  const sent = storageService.getItem<unknown>(StorageKeys.adLeads)
  if (leadAlreadySent(sent, 'fb', slot)) return
  start()
  window.fbq?.('track', 'Lead', leadParams(FUNNEL_PARAMS, FUNNEL_TYPE, LEAD_VALUES), {
    eventID: leadEventId(slot, deviceId()),
  })
  window.lintrk?.('track', { conversion_id: LI_CONVERSION.lead })
  storageService.setItem(StorageKeys.adLeads, withLeadSent(sent, 'fb', slot))
}

/**
 * Активация: шортлист по своему продукту на экране — человек увидел ценность, ради которой пришёл.
 * Стандартное событие Меты между Lead и InitiateCheckout, чтобы кампании могли оптимизироваться на
 * «дошёл до результата», а не на «оставил email». Зовётся из той же точки, что shortlist_shown в
 * продуктовой аналитике — один раз на прогон, не на каждый визит страницы.
 * LinkedIn здесь молчит: у нас есть ровно два их conversion id (лид и покупка), третьего не выдано.
 */
export const pixelsShortlistReady = () => {
  if (!ENABLED || !marketingAllowed()) return
  start()
  window.fbq?.('track', 'CompleteRegistration', FUNNEL_PARAMS)
}

/** «Когда попал на форму оплаты» — открытие чекаута, после гейта. */
export const pixelsInitiateCheckout = () => {
  if (!ENABLED || !marketingAllowed()) return
  start()
  window.fbq?.('track', 'InitiateCheckout', FUNNEL_PARAMS)
}

/**
 * Покупка. value = 70% суммы (REPORTED_SHARE; у GA — 92%, см. ga.ts), валюта — доллары, других в
 * чекауте нет.
 *
 * eventID = order_id шлюза: тот же ключ, которым /thank-you уже гасит повтор в браузере, и тот, что
 * серверный Purchase через Conversions API сможет прислать из POST /api/pay/returned для дедупа.
 * `funnel_at_entry` — воронка из записи поиска в момент входа (SearchRecord.funnel): покупка воронки 3
 * приходит через дни, и cookie к этому времени может сказать уже другое (см. Home/PurchaseDoor.tsx).
 */
export const pixelsPurchase = (
  totalUsd: number,
  order: { orderId: string | null; funnelAtEntry: number | null },
) => {
  if (!ENABLED || !marketingAllowed()) return
  start()
  const value = Math.round(totalUsd * REPORTED_SHARE * 100) / 100
  const params = {
    value,
    currency: 'USD',
    ...FUNNEL_PARAMS,
    ...(order.funnelAtEntry ? { funnel_at_entry: order.funnelAtEntry } : {}),
  }
  if (order.orderId) window.fbq?.('track', 'Purchase', params, { eventID: order.orderId })
  else window.fbq?.('track', 'Purchase', params)
  window.lintrk?.('track', { conversion_id: LI_CONVERSION.purchase })
}
